import { styled } from '@rybr/styled-modules'
import { useLocale } from 'next-intl'
import React, { useMemo } from 'react'

import { Carousel } from '@ntc/components/Carousel'
import { SanityImage, SanityImageProps } from '@ntc/components/SanityImage'

import { BADGE } from '@constants/constants.product'
import { UseConstructorRecommendationProps, useConstructorRecommendations } from '@hooks/useConstructorRecommendations'
import { analytics } from '@lib/analytics/analytics-click-events'
import { FormattedColorTheme } from '@lib/sanity/sanity-utils'
import { theme } from '@src/theme'
import { ProductTile, ProductTileProps } from './ProductTile'

import styles from './RecommendationCarouselWithHeroBanner.module.scss'

export type RecommendationCarouselWithHeroBannerProps = {
  promotion?: { title: string } | null
  colorTheme: FormattedColorTheme
  imageOverlapPercent?: number | null
  image?: SanityImageProps | null
  title?: string | null
  constructorConfigs: UseConstructorRecommendationProps
}

// width = (100% - #gaps * gap-size)/# items to show
const calcWidth = (columnCount: number, gap: string) =>
  `calc((100% - ${Math.ceil(columnCount) - 1} * ${gap}) / ${columnCount})`

const MainContainer = styled.div<{ columnCount: number }>(styles.MainContainer, ({ columnCount }) => ({
  width: calcWidth(columnCount > 4 ? 4.3 : 4, theme['size-4'])
}))

const BannerContainer = styled.div<{ overlapPercent: number }>(styles.BannerContainer, ({ overlapPercent }) => ({
  marginBottom: `${-1 * overlapPercent}%`
}))
const Title = styled.h2(styles.Title)
const RecommendationContainer = styled.div(styles.RecommendationContainer)
const CarouselContainer = styled.div(styles.CarouselContainer)

const LoadingProductTile = styled.div(styles.LoadingProductTile)

export const RecommendationCarouselWithHeroBanner: React.FC<RecommendationCarouselWithHeroBannerProps> = ({
  title,
  imageOverlapPercent,
  image,
  constructorConfigs
}) => {
  const locale = useLocale()
  const { data, isLoading } = useConstructorRecommendations(constructorConfigs)
  const recommendations = data?.items
  const resultId = data?.resultId

  const productTileProps = useMemo(
    () =>
      (recommendations ?? []).map<
        ProductTileProps & {
          id: string
          'data-cnstrc-item'?: string
          'data-cnstrc-item-id'?: string
          'data-cnstrc-item-name'?: string
          'data-cnstrc-strategy-id'?: string
        }
      >((product, index) => ({
        id: product.productId,
        url: product.url,
        image: {
          src: product.imageSrc,
          width: 284,
          height: 284
        },
        brandName: product.brandName,
        description: product.description,
        listPrice: product.originalPrice,
        salePrice: product.mainPrice,
        badge: product.badge,
        'data-cnstrc-item': 'recommendation',
        'data-cnstrc-item-id': product.productId,
        'data-cnstrc-item-name': product.description,
        'data-cnstrc-strategy-id': product.strategyId,
        onClick: () =>
          analytics.carouselProductClick({
            articleNumber: product.productId,
            productID: product.omniItemId ?? '',
            manufacturerNumber: product.modelId ?? '',
            itemNumber: product.mmsItemNumber ?? '',
            brand: product.brandName ?? '',
            description: product.description ?? '',
            productSellingPrice:
              (product.originalPrice === product.mainPrice ? product.originalPrice : product.mainPrice) ?? 0,
            productWasPrice: product.originalPrice ?? 0,
            priceType: product.badge == BADGE.clearance ? 'L' : product.badge === BADGE.priceDrop ? 'P' : '',
            carouselName: title ?? '',
            position: `1*${index + 1}`
          })
      })),
    [recommendations, title]
  )

  const overlapPercent = useMemo(
    () => ((imageOverlapPercent ?? 0) * (image?.height ?? 1)) / (image?.width ?? 1),
    [image?.height, image?.width, imageOverlapPercent]
  )

  return !isLoading && productTileProps.length === 0 ? null : (
    <MainContainer $columnCount={productTileProps.length}>
      {image == null ? null : (
        <BannerContainer $overlapPercent={overlapPercent}>
          <SanityImage
            className={styles.BannerImage}
            {...image}
          />
        </BannerContainer>
      )}
      <RecommendationContainer
        data-cnstrc-recommendations
        data-cnstrc-recommendations-pod-id={constructorConfigs.podId?.[locale]}
        data-cnstrc-num-results={productTileProps.length}
        data-cnstrc-result-id={resultId}
      >
        <CarouselContainer>
          {title ? <Title>{title}</Title> : null}
          <Carousel
            indexes={() => null}
            mainContainerStyle={styles.Carousel}
            leftArrowStyle={styles.CarouselLeftArrow}
            rightArrowStyle={styles.CarouselRightArrow}
            slideStyle={styles.CarouselSlide}
            slidesContainerStyle={styles.CarouselSlideContainer}
            onArrowClick={({ isLeft }) =>
              isLeft
                ? analytics.carouselLeftClick({ cursorLinkType: 'Arrow Left Click', cursorLinkName: title ?? '' })
                : analytics.carouselRightClick({ cursorLinkType: 'Arrow Right Click', cursorLinkName: title ?? '' })
            }
          >
            {isLoading
              ? new Array(4).fill(null).map((_, i) => <LoadingProductTile key={i} />)
              : productTileProps.map((props, i) => (
                  <ProductTile
                    {...props}
                    key={`${i}-${props.id}`}
                  />
                ))}
          </Carousel>
        </CarouselContainer>
      </RecommendationContainer>
    </MainContainer>
  )
}
